'use client';

import Link from 'next/link';

import { useSubscription } from '../hooks/useSubscription';

interface SubscriptionUpsellProps {
  /** Current coach usage out of the user's monthly cap. */
  usage?: { used: number; limit: number };
}

/**
 * Contextual upgrade CTA — shown on the dashboard next to the coach usage
 * meter. Renders when the user is on `free` or `starter` AND has used at
 * least 70% of their messages this month. Hidden otherwise so it doesn't
 * nag.
 *
 * The link goes to the billing page rather than triggering checkout
 * inline — gives users a chance to actually compare plans first.
 */
export function SubscriptionUpsell({ usage }: SubscriptionUpsellProps) {
  const { data } = useSubscription();
  if (!data) return null;
  if (data.tier !== 'free' && data.tier !== 'starter') return null;
  if (!usage || usage.limit < 0) return null;

  const ratio = usage.limit === 0 ? 1 : usage.used / usage.limit;
  if (ratio < 0.7) return null;

  const ceiling = data.tier === 'free' ? 'Pro' : 'Elite';
  return (
    <Link
      href="/billing"
      className="block rounded-lg border border-coral/40 bg-coral/5 p-3 text-xs text-foreground hover:bg-coral/10"
    >
      <div className="font-medium">
        {usage.used}/{usage.limit} messages used this month
      </div>
      <div className="mt-0.5 text-muted-foreground">
        Upgrade to {ceiling} for {ceiling === 'Elite' ? 'unlimited' : 'more'} →
      </div>
    </Link>
  );
}
